import React from "react";
// Relative, not "@/...": real modules under Jest, no alias there. `checkoutErrorMessage`
// is deliberately the REAL one — it is the single code→sentence map a refused checkout
// already uses, and a per-suite stub of it would let this component's refusal wording
// drift from the storefront's.
import { formatMoneyIntl } from "../../lib/formatMoney";
import { checkoutErrorMessage } from "../../lib/checkoutErrors";
import { useLanguage, useT } from "@/lib/LanguageContext";

/**
 * PriceBreakdown — the ONE pre-payment breakdown of every public checkout (BUG-048).
 *
 * PURE PRESENTATION OF A SERVER QUOTE. It computes nothing: `quote` is
 * `useCheckoutQuote`'s payload, i.e. `GET /api/public/checkout-breakdown`, i.e. the same
 * discount and tax engines the charge itself runs through. The rows it prints are the
 * rows that decide the amount — item price, discount, one line per NAMED tax with its
 * rate, and the total that will actually be charged — so a buyer never has to work
 * backwards from a single figure to check whether their code was applied (the sheet's
 * complaint), and never meets the taxed total for the first time on Stripe's page
 * (BUG-054).
 *
 * `quote === null` MEANS NOT KNOWN, and it is rendered, not hidden: either the read is in
 * flight or it failed. The page then says the total appears at the payment step instead of
 * leaving the item price standing as if it were the amount payable. A refused promo code
 * (400 PROMO_INVALID) arrives as `error` and is shown through the same stable-code map a
 * refused checkout uses, so the buyer reads it before paying rather than after.
 *
 * NO COMMISSION IS EVER RENDERED. The endpoint's payload also carries the platform's
 * commission on the sale (`commission_cents`, `fee_pct`, `net_to_practitioner_cents`) —
 * that is the practice's business, not the buyer's, and this component reads none of it.
 *
 * Props:
 *   quote     the breakdown payload, or null when unknown
 *   error     the axios error of a failed/refused quote, or null
 *   currency  ISO currency of the purchase — the OFFERING's, handed in by the page
 *             (`quote.currency` says the same thing; the prop keeps the amounts readable
 *             while a quote is still in flight)
 *   compact   drop the item/subtotal rows and print only tax lines + total (the booking
 *             summary, which already shows the price of the slot above)
 *   testId    data-testid of the block, defaulting to "price-breakdown"
 */
export default function PriceBreakdown({
  quote,
  error = null,
  currency,
  compact = false,
  testId = "price-breakdown",
}) {
  const { lang } = useLanguage();
  const t = useT("public");
  const code = quote?.currency || currency;
  const fmt = (cents) => formatMoneyIntl(cents, code, lang);

  if (!quote) {
    return (
      <div className="text-xs text-eh-muted" data-testid={`${testId}-unavailable`}>
        {error ? checkoutErrorMessage(error, t, t("breakdown.unavailable"))
               : t("breakdown.unavailable")}
      </div>
    );
  }

  const taxLines = (quote.tax_breakdown || []).filter((l) => Number(l.amount_cents) > 0);
  const discount = Number(quote.discount_cents || 0);
  // BUG-048 part 1: `item_cents` is the advertised, UN-decomposed sticker price — on a
  // tax-inclusive quote that price already equals `total_charged_cents` (that is what
  // "tax-inclusive" means), so printing it on the item row makes "item + tax" overshoot
  // the total by the tax itself. `subtotal_cents` is the genuinely pre-tax figure the tax
  // lines are computed FROM (`platform_helpers.split_amount_over_tax_lines`), and it is
  // always related to the sticker price by `subtotal_cents + discount_cents == item_cents`
  // (exactly, no rounding — true in both the inclusive and exclusive branches; see the
  // `_quote_or_422` caller in `platform_settings_routes.py`). So `subtotal_cents +
  // discount` reconstructs the pre-tax, PRE-DISCOUNT item price without reprinting the
  // post-discount taxable base that the (discount-gated) subtotal row already shows —
  // the two rows differ by exactly `discount`, never coincide, and the chain
  // `item − discount = subtotal`, `subtotal + tax = total` holds whether or not a promo
  // code was used. Tax-exclusive quotes are untouched (BUG-048 part 2 is not this fix's
  // business): there, `item_cents` already IS the pre-tax figure and the current rows
  // already reconcile (proven by the exclusive control in
  // PriceBreakdown.taxInclusive.test.js).
  const item = quote.tax_inclusive
    ? Number(quote.subtotal_cents) + discount
    : Number(quote.item_cents ?? quote.subtotal_cents);

  return (
    <div
      className="bg-eh-cream/40 border border-eh-border-soft rounded-xl p-3 text-xs space-y-1"
      data-testid={testId}
    >
      {!compact && (
        <div className="flex justify-between text-eh-muted">
          <span>
            {t("breakdown.item")}
            {/* The row a buyer reads as "the item's price" carries its own inclusive-tax
                qualifier, rather than a caption floated below the total that a reader has
                no reason to connect back to this specific figure (BUG-048 part 1). */}
            {quote.tax_inclusive && (
              <span className="ml-1 text-[11px]">{t("breakdown.taxIncludedNote")}</span>
            )}
          </span>
          <span data-testid={`${testId}-item`}>{fmt(item)}</span>
        </div>
      )}
      {discount > 0 && (
        <div className="flex justify-between text-eh-sage-deep" data-testid={`${testId}-discount`}>
          <span>{t("breakdown.discount", { code: quote.promo_code || "" })}</span>
          <span>−{fmt(discount)}</span>
        </div>
      )}
      {!compact && discount > 0 && (
        <div className="flex justify-between text-eh-muted">
          <span>{t("breakdown.subtotal")}</span>
          <span data-testid={`${testId}-subtotal`}>{fmt(quote.subtotal_cents)}</span>
        </div>
      )}
      {taxLines.map((line, i) => (
        <div key={`${line.name}-${i}`} className="flex justify-between text-eh-muted"
             data-testid={`${testId}-tax-${i}`}>
          <span>{t("breakdown.taxLine", { name: line.name, rate: line.rate_pct })}</span>
          <span>{fmt(line.amount_cents)}</span>
        </div>
      ))}
      <div className="flex justify-between font-medium text-eh-ink pt-1 border-t border-eh-border-soft">
        <span>{t("breakdown.total")}</span>
        <span data-testid={`${testId}-total`}>{fmt(quote.total_charged_cents)}</span>
      </div>
      {/* Non-compact: the note now lives on the item row itself, above. Compact mode
          renders no item row at all (BUG-048, out of scope here — see PriceBreakdown.jsx
          module docstring), so this is the only place left to say the total is
          tax-inclusive; unchanged from before this fix for that surface. */}
      {compact && quote.tax_inclusive && (
        <div className="text-eh-muted">{t("breakdown.taxIncludedNote")}</div>
      )}
    </div>
  );
}
